// 用户注册页面 - 现代简约风格
import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { UserPlus, Mail, Lock, User, Eye, EyeOff } from 'lucide-react';

const Register: React.FC = () => {
  const { referralCode } = useParams<{ referralCode?: string }>() // 从URL获取推荐码
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [referralCodeInput, setReferralCodeInput] = useState(referralCode || ''); // 推荐码输入框
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(true); // 默认选中
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState<'client' | 'server' | ''>('');
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 客户端验证
    if (!username.trim()) {
      setError('Please enter a username');
      setErrorType('client');
      return;
    }
    if (username.length < 3) {
      setError('Username must be at least 3 characters long');
      setErrorType('client');
      return;
    }
    if (!email.trim()) {
      setError('Please enter your email address');
      setErrorType('client');
      return;
    }
    if (!email.includes('@') || !email.includes('.')) {
      setError('Please enter a valid email address');
      setErrorType('client');
      return;
    }
    if (!password.trim()) {
      setError('Please enter a password');
      setErrorType('client');
      return;
    }
    if (!confirmPassword.trim()) {
      setError('Please confirm your password');
      setErrorType('client');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setErrorType('client');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setErrorType('client');
      return;
    }

    if (!agreeToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy to register');
      setErrorType('client');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/register', {
        email,
        password,
        username,
        referralCode: referralCodeInput.trim() || undefined, // 包含推荐码
      });

      // 注册成功，显示邮箱验证提示
      setShowEmailVerificationModal(true);
    } catch (err: any) {
      let errorMessage = 'Something went wrong. Please try again.';

      if (err.response) {
        const status = err.response.status;
        const serverMessage = err.response?.data?.error?.message || err.response?.data?.message;

        switch (status) {
          case 400:
            errorMessage = serverMessage || 'Invalid registration information.';
            break;
          case 409:
            errorMessage = 'Email already registered. Please try logging in or use a different email.';
            break;
          case 422:
            errorMessage = 'Invalid email format or password too weak.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = serverMessage || `Registration failed (Error ${status}). Please try again.`;
        }
      } else if (err.request) {
        errorMessage = 'Network error. Please check your internet connection.';
      }

      setError(errorMessage);
      setErrorType('server');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailVerificationConfirm = () => {
    setShowEmailVerificationModal(false);
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 flex items-center justify-center px-4 relative overflow-hidden">
      {/* 网格背景 */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 opacity-15" style={{
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.2) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.2) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 w-full max-w-sm border border-white/20 shadow-2xl animate-in slide-in-from-bottom-4 duration-700">
        {/* Logo和标题 */}
        <div className="text-center mb-6">
          <div className="relative mb-3">
            <h1 className="text-xl font-bold text-white text-center animate-in fade-in duration-1000 delay-300">ModelPool</h1>
            <img src={`${process.env.PUBLIC_URL}/mplogo.png`} alt="ModelPool" className="absolute right-full top-1/2 -translate-y-1/2 mr-2 h-8 w-8 animate-in spin-in duration-1000 delay-500" style={{ left: 'calc(50% - 80px)' }} />
          </div>
          <p className="text-white/80 text-xs animate-in fade-in duration-1000 delay-700">Create your ModelPool account</p>
        </div>

        {/* 注册表单 */}
        <form onSubmit={handleSubmit} className="space-y-4 animate-in fade-in duration-1000 delay-900" noValidate>
          {error && (
            <div className="bg-red-500/10 border border-red-400/30 text-red-300 px-4 py-3 rounded-lg text-sm backdrop-blur-sm flex items-center space-x-2 animate-in slide-in-from-top-2 duration-500">
              <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* 用户名输入 */}
          <div className="space-y-1 animate-in slide-in-from-left-4 duration-600 delay-1000">
            <label className="block text-white/90 text-xs font-medium">Username</label>
            <div className="relative">
              <input
                type="text"
                required
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  if (error && errorType === 'client') {
                    setError('');
                    setErrorType('');
                  }
                }}
                className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm text-sm transition-all duration-300 hover:border-white/40"
                placeholder="Choose a username"
              />
            </div>
          </div>

          {/* 邮箱输入 */}
          <div className="space-y-1 animate-in slide-in-from-right-4 duration-600 delay-1100">
            <label className="block text-white/90 text-xs font-medium">Email Address</label>
            <div className="relative">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error && errorType === 'client') {
                    setError('');
                    setErrorType('');
                  }
                }}
                className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm text-sm transition-all duration-300 hover:border-white/40"
                placeholder="Enter your email"
              />
            </div>
          </div>

          {/* 密码输入 */}
          <div className="space-y-1 animate-in slide-in-from-left-4 duration-600 delay-1200">
            <label className="block text-white/90 text-xs font-medium">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error && errorType === 'client') {
                    setError('');
                    setErrorType('');
                  }
                }}
                className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 px-3 py-2 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm text-sm transition-all duration-300 hover:border-white/40"
                placeholder="Create a password"
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white/80 transition duration-200 hover:scale-110"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-white/60 text-xs mt-1">Minimum 6 characters required</p>
          </div>

          {/* 确认密码输入 */}
          <div className="space-y-1 animate-in slide-in-from-right-4 duration-600 delay-1300">
            <label className="block text-white/90 text-xs font-medium">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                required
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (error && errorType === 'client') {
                    setError('');
                    setErrorType('');
                  }
                }}
                className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 px-3 py-2 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm text-sm transition-all duration-300 hover:border-white/40"
                placeholder="Confirm your password"
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white/80 transition duration-200 hover:scale-110"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {password && confirmPassword && password !== confirmPassword && (
              <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
            )}
          </div>

          {/* 推荐码输入 */}
          <div className="space-y-1 animate-in slide-in-from-left-4 duration-600 delay-1400">
            <label className="block text-white/90 text-xs font-medium">
              Referral Code <span className="text-white/60">(Optional)</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={referralCodeInput}
                onChange={(e) => setReferralCodeInput(e.target.value.toUpperCase())}
                className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm text-sm transition-all duration-300 hover:border-white/40"
                placeholder="Enter referral code"
                maxLength={8}
              />
            </div>
            {referralCode && (
              <p className="text-green-400 text-xs animate-in fade-in duration-500">🎁 Referral code applied! You'll get bonus rewards.</p>
            )}
            {/* 同意条款复选框 */}
            <div className="flex items-start space-x-2 mt-3 animate-in slide-in-from-right-4 duration-600 delay-1500">
              <input
                id="agree-terms"
                type="checkbox"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                className="h-3 w-3 text-blue-600 border-white/30 rounded focus:ring-blue-500 focus:ring-1 bg-white/10 mt-0.5"
              />
              <label htmlFor="agree-terms" className="text-xs text-white/90">
                I agree to the <a href="/terms" target="_blank" className="text-blue-300 hover:text-blue-200 underline transition-colors">Terms of Service</a> and <a href="/privacy" target="_blank" className="text-blue-300 hover:text-blue-200 underline transition-colors">Privacy Policy</a>
              </label>
            </div>
          </div>



          {/* 注册按钮 */}
          <button
            type="submit"
            disabled={loading || !agreeToTerms}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 animate-in slide-in-from-bottom-4 duration-600 delay-1600 hover:scale-105 active:scale-95"
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <div className="spinner"></div>
                Creating account...
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <UserPlus className="h-4 w-4" />
                Create Account
              </div>
            )}
          </button>

          {/* 登录链接 */}
          <div className="text-center mt-4 animate-in fade-in duration-600 delay-1600">
            <span className="text-white/70 text-xs">Already have an account? </span>
            <Link to="/login" className="text-blue-400 hover:text-blue-300 text-xs font-medium transition-all duration-200 hover:underline">
              Sign In
            </Link>
          </div>
        </form>
      </div>
      {/* 邮箱验证提示弹窗 */}
      {showEmailVerificationModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-300">
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 w-full max-w-md mx-4 border border-white/20 shadow-2xl animate-in zoom-in-95 duration-500">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-in zoom-in duration-700 delay-200">
                <Mail className="w-8 h-8 text-blue-400 animate-in fade-in duration-500 delay-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2 animate-in slide-in-from-bottom-2 duration-500 delay-300">Check Your Email</h3>
              <p className="text-white/80 mb-6 text-sm animate-in fade-in duration-500 delay-400">
                We've sent a verification email to <span className="font-medium text-blue-300">{email}</span>.
                Please check your inbox and click the verification link to activate your account.
              </p>
              <button
                onClick={handleEmailVerificationConfirm}
                className="w-full bg-blue-600 text-white font-medium py-2.5 px-4 rounded-lg hover:bg-blue-700 transition-all duration-300 animate-in slide-in-from-bottom-4 duration-500 delay-500 hover:scale-105"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

  );
};

export default Register;